/**
 * ユーザー管理 API（/api/admin/users、基金のみ）
 * feature ごとに「型・クエリキー・hooks」をこのファイルにまとめるのが標準パターン。
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { OrganizationType } from '@/auth/types';

export interface UserDto {
  id: number;
  loginId: string;
  name: string;
  organizationType: OrganizationType;
  businessOperatorId: number | null;
  isActive: boolean;
  lastLoginAt: string | null;
  /** 2段階認証：必須（true）／任意（false） */
  mfaRequired: boolean;
  /** 2段階認証：有効（認証アプリ登録済み） */
  mfaEnabled: boolean;
  mfaEnabledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserListFilter {
  q?: string;
  organizationType?: OrganizationType | '';
  isActive?: 'true' | 'false' | '';
}

export interface UserCreateInput {
  loginId: string;
  password: string;
  name: string;
  organizationType: OrganizationType;
  businessOperatorId: number | null;
  isActive: boolean;
  mfaRequired: boolean;
}
export type UserUpdateInput = Partial<UserCreateInput>;

/** クエリキーは feature 名から始める（invalidate を楽にするため） */
export const userKeys = {
  all: ['admin-users'] as const,
  list: (f: UserListFilter) => [...userKeys.all, 'list', f] as const,
  detail: (id: number) => [...userKeys.all, 'detail', id] as const,
};

export function useUsers(filter: UserListFilter) {
  return useQuery({
    queryKey: userKeys.list(filter),
    queryFn: ({ signal }) => api.get<UserDto[]>('/admin/users', { ...filter }, { signal }),
    placeholderData: keepPreviousData,
  });
}

export function useUser(id: number | null) {
  return useQuery({
    queryKey: userKeys.detail(id ?? 0),
    queryFn: ({ signal }) => api.get<UserDto>(`/admin/users/${id}`, undefined, { signal }),
    enabled: id != null,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UserCreateInput) => api.post<UserDto>('/admin/users', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useUpdateUser(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UserUpdateInput) => api.patch<UserDto>(`/admin/users/${id}`, input),
    onSuccess: (data) => {
      qc.setQueryData(userKeys.detail(id), data);
      return qc.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/admin/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

/** 2段階認証のリセット（端末紛失・機種変更時） */
export function useResetUserMfa(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<UserDto>(`/admin/users/${id}/mfa/reset`),
    onSuccess: (data) => {
      qc.setQueryData(userKeys.detail(id), data);
      return qc.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}
